import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiPlus, FiSearch, FiArrowLeft, FiX, FiAlertTriangle,
  FiCheckCircle, FiShoppingCart, FiTrash2, FiPrinter
} from 'react-icons/fi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  getPurchases, getPurchase, createPurchase,
  checkPurchaseBlockers, anularPurchase,
  getProviders, getWarehouses, getProducts,
  getConfig, getFamilies, getEmpresaInfo,
  getProductPresentations
} from '../../../services/api';
import '../../../styles/ModuleTable.css';
import '../../../styles/ComprasMaintenance.css';

// ── helpers ────────────────────────────────────────────────
const fmtS  = n => `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN  = (n, d = 2) => parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: d, maximumFractionDigits: d });
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDate = s => s ? new Date(s).toLocaleDateString('es-PE', { timeZone: 'America/Lima' }) : '—';
// due_date (aquí `dueDate`, del input de fecha) es una fecha calendario pura
// (sin hora) — timeZone la correría un día atrás; se reformatea el string
// directo, sin pasar por Date.
const fmtDateOnly = s => { if (!s) return '—'; const m = String(s).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s).slice(0, 10); };

const DOC_TYPES = ['FACTURA', 'BOLETA', 'NOTA DÉBITO', 'NOTA CRÉDITO', 'NINGUNO'];
const PAYMENT_METHODS = ['EFECTIVO', 'YAPE', 'PLIN', 'TRANSFERENCIA BANCARIA', 'TARJETA CRED. O DEBITO'];

// ── Badge de estado de pago ────────────────────────────────
function BadgePago({ status }) {
  const cfg = {
    paid:    { cls: 'badge-purchase-paid',    label: 'CONTADO' },
    credit:  { cls: 'badge-purchase-credit',  label: 'CRÉDITO' },
    partial: { cls: 'badge-purchase-partial', label: 'PARCIAL' },
  };
  const s = cfg[status] || cfg.credit;
  return <span className={`badge-purchase-status ${s.cls}`}>{s.label}</span>;
}

function BadgeEstado({ status }) {
  const cfg = {
    completed: { cls: 'badge-completed', label: 'ACTIVA' },
    cancelled: { cls: 'badge-cancelled', label: 'ANULADA' },
    pending:   { cls: 'badge-purchase-credit', label: 'PENDIENTE' },
  };
  const s = cfg[status] || cfg.completed;
  return <span className={`badge-purchase-status ${s.cls}`}>{s.label}</span>;
}

// ════════════════════════════════════════════════════════════
// MODAL ANULACIÓN
// ════════════════════════════════════════════════════════════
function ModalAnulacion({ purchaseId, businessId, onClose, onAnulado }) {
  const [loading, setLoading]   = useState(true);
  const [checking, setChecking] = useState(false);
  const [data, setData]         = useState(null);
  const [error, setError]       = useState('');

  useEffect(() => {
    checkPurchaseBlockers(purchaseId, businessId)
      .then(res => setData(res.data?.data))
      .catch(e  => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [purchaseId, businessId]);

  const handleConfirm = async () => {
    setChecking(true);
    try {
      await anularPurchase(purchaseId, businessId);
      onAnulado();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="mt-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="mt-modal cm-modal-blockers" style={{ maxWidth: 660 }}>
        <div className="mt-modal-header">
          <span>
            {loading ? 'Verificando...' : (data?.blockers?.length || data?.paymentBlocker) ? '⚠️ ANULACIÓN BLOQUEADA' : '⚠️ CONFIRMAR ANULACIÓN'}
            {data?.purchase && ` — ${data.purchase.purchase_number}`}
          </span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mt-modal-body">
          {loading && <div className="mt-loading">Verificando operaciones asociadas...</div>}

          {error && <div style={{ color: '#dc2626', fontSize: 12.5, padding: '8px 0' }}>{error}</div>}

          {!loading && data?.paymentBlocker && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
              padding: '10px 12px', fontSize: 12.5, color: '#991b1b', marginBottom: 10
            }}>
              {data.paymentBlocker.message}
            </div>
          )}

          {!loading && data?.blockers?.length > 0 && (
            <>
              <p style={{ fontSize: 12.5, color: '#1e293b', marginBottom: 8 }}>
                Esta compra tiene <b>{data.blockers.length} operación(es)</b> realizadas sobre los
                productos recibidos. Debes anularlas primero:
              </p>
              <table className="cm-blocker-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Referencia</th>
                    <th>Producto</th>
                    <th>Fecha</th>
                    <th style={{ textAlign: 'right' }}>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {data.blockers.map((b, i) => (
                    <tr key={i}>
                      <td><span className={`si-mov-type si-mov-${b.movement_type}`}>{b.movement_type.toUpperCase()}</span></td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11.5 }}>{b.reference}</td>
                      <td>{b.product_name}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{fmtDate(b.moved_at)}</td>
                      <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>
                        {fmtN(b.qty_moved, 3)} {b.unit_measure}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: 11.5, color: '#64748b', marginTop: 10 }}>
                ➤ Anula primero esas operaciones desde sus módulos correspondientes y vuelve a intentarlo.
              </p>
            </>
          )}

          {!loading && data?.canCancel && (
            <>
              <p style={{ fontSize: 12.5, color: '#1e293b', marginBottom: 8 }}>
                ¿Confirmas la anulación de esta compra? Esta acción no se puede deshacer.
              </p>
              {data.purchase && (
                <div className="cm-cancel-summary">
                  <b>Al confirmar se revertirá:</b>
                  <ul style={{ margin: '6px 0 0 16px', listStyle: 'disc' }}>
                    {/* El detalle de ítems no está en checkBlockers, solo el resumen */}
                    <li>Todos los ingresos de stock generados por esta compra</li>
                    <li>La cuenta por pagar asociada (si no tiene abonos)</li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
        <div className="mt-modal-footer">
          {!loading && data?.canCancel && (
            <button className="btn-modificar" style={{ background: '#dc2626' }}
              onClick={handleConfirm} disabled={checking}>
              {checking ? 'Anulando...' : 'Confirmar anulación'}
            </button>
          )}
          <button className="btn-salir" onClick={onClose}>
            {(data?.blockers?.length || data?.paymentBlocker) ? 'Entendido' : 'Cancelar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// FORMULARIO NUEVA COMPRA
// ════════════════════════════════════════════════════════════
function FormNuevaCompra({ businessId, userId, onSaved, onClose }) {
  // Datos del formulario
  const [providerSearch, setProviderSearch] = useState('');
  const [providers, setProviders]           = useState([]);
  const [provSuggestions, setProvSuggestions] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [docType,    setDocType]    = useState('FACTURA');
  const [serie,      setSerie]      = useState('');
  const [docNumber,  setDocNumber]  = useState('');
  const [purchaseDate, setPurchaseDate] = useState(today());
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [paymentType, setPaymentType] = useState('paid');
  const [paymentMethod, setPaymentMethod] = useState('EFECTIVO');
  const [dueDate,    setDueDate]    = useState('');

  // Catálogo de productos
  const [products,      setProducts]      = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [families,      setFamilies]      = useState([]);
  const [items,         setItems]         = useState([]);

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  useEffect(() => {
    Promise.all([
      getWarehouses(businessId),
      getProducts(businessId),
      getProviders(businessId),
      getFamilies(businessId)
    ]).then(([wRes, pRes, prRes, fRes]) => {
      const whs = wRes.data?.data || [];
      setWarehouses(whs);
      if (whs.length) setWarehouseId(String(whs.find(w => w.is_default)?.id || whs[0].id));
      setProducts(pRes.data?.data || []);
      setProviders(prRes.data?.data || []);
      setFamilies(fRes.data?.data || []);
    });
  }, [businessId]);

  // Búsqueda proveedor
  useEffect(() => {
    if (providerSearch.length < 2) { setProvSuggestions([]); return; }
    const q = providerSearch.toLowerCase();
    setProvSuggestions(providers.filter(p =>
      p.ruc?.includes(q) || p.name?.toLowerCase().includes(q)
    ).slice(0, 8));
  }, [providerSearch, providers]);

  // Productos filtrados para catálogo
  const catalogFiltered = products.filter(p => {
    if (!catalogSearch) return true;
    const q = catalogSearch.toLowerCase();
    return p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q);
  });

  // IDs ya agregados
  const addedIds = new Set(items.map(i => i.product_id));

  // Agregar producto al detalle
  const addProduct = async (p) => {
    if (addedIds.has(p.id)) return;
    const um = p.unit_measure || 'KG';
    let presentationOptions = [];
    if (!p.special_condition) {
      try {
        const res = await getProductPresentations(p.id, businessId);
        presentationOptions = res.data?.data || [];
      } catch { presentationOptions = []; }
    }
    setItems(prev => [...prev, {
      product_id: p.id, sku: p.sku,
      product_name: p.name, unit_measure: um,
      baseUnitMeasure: um,
      baseCostPrice: parseFloat(p.cost_price || 0),
      baseSalePrice: parseFloat(p.unit_price || 0),
      factor: 1,
      presentationName: null,
      presentationOptions,
      special_condition: p.special_condition,
      salePriceRef: parseFloat(p.unit_price || 0),
      qty: '', qty_unidad: '', unit_price: parseFloat(p.cost_price || 0).toFixed(2),
      discount: '0.00', stock: p.stock
    }]);
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const updateItem = (idx, field, val) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  };

  // Cambiar presentación de venta de una línea de compra
  const changePresentation = (idx, presKey) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      if (presKey === 'base') {
        return {
          ...it, factor: 1, unit_measure: it.baseUnitMeasure, presentationName: null,
          unit_price: it.baseCostPrice.toFixed(2), salePriceRef: it.baseSalePrice
        };
      }
      const pres = (it.presentationOptions || []).find(pp => String(pp.id) === String(presKey));
      if (!pres) return it;
      const factor = parseInt(pres.factor, 10) || 1;
      return {
        ...it,
        factor, unit_measure: pres.name, presentationName: pres.name,
        unit_price: (it.baseCostPrice * factor).toFixed(2),
        salePriceRef: parseFloat(pres.sale_price),
      };
    }));
  };

  // Cálculo de totales
  const calcItem = (it) => {
    const qty   = parseFloat(it.qty     || 0);
    const price = parseFloat(it.unit_price || 0);
    const disc  = parseFloat(it.discount  || 0);
    return Math.max(0, qty * price - disc);
  };
  const subtotal = items.reduce((s, it) => s + calcItem(it), 0);
  const discTotal = items.reduce((s, it) => s + parseFloat(it.discount || 0), 0);
  const total     = subtotal;

  // Guardar
  const handleSave = async () => {
    setError('');
    if (!selectedProvider)    return setError('Selecciona un proveedor');
    if (!warehouseId)         return setError('Selecciona el almacén destino');
    if (!items.length)        return setError('Agrega al menos un producto');
    const badQty = items.find(it => !it.qty || parseFloat(it.qty) <= 0);
    if (badQty) return setError(`Cantidad inválida en "${badQty.product_name}"`);
    if (paymentType === 'credit' && !dueDate) return setError('Indica la fecha de vencimiento para crédito');

    setSaving(true);
    try {
      const payload = {
        provider_id: selectedProvider.id,
        warehouse_id: parseInt(warehouseId),
        document_type: docType,
        serie: docType !== 'NINGUNO' ? serie : null,
        doc_number: docType !== 'NINGUNO' ? docNumber : null,
        purchase_date: purchaseDate,
        payment_status: paymentType,
        payment_method: paymentType === 'paid' ? paymentMethod : null,
        due_date: paymentType === 'credit' ? dueDate : null,
        subtotal: parseFloat(subtotal.toFixed(2)),
        discount: parseFloat(discTotal.toFixed(2)),
        igv: 0,
        total: parseFloat(total.toFixed(2)),
        notes: '',
        items: items.map(it => {
          const factor = it.factor || 1;
          const base = {
            product_id:   it.product_id,
            sku:          it.sku,
            barcode:      it.sku,
            product_name: it.product_name,
            unit_measure: it.baseUnitMeasure || it.unit_measure,
            special_condition: it.special_condition,
            qty:          parseFloat(it.qty) * factor,
            qty_unidad:   parseInt(it.qty_unidad || 0),
            unit_price:   factor > 1 ? parseFloat(it.unit_price) / factor : parseFloat(it.unit_price),
            discount:     parseFloat(it.discount || 0),
            subtotal:     parseFloat(calcItem(it).toFixed(2))
          };
          if (factor > 1) {
            base.quantity_presentation   = parseFloat(it.qty);
            base.unit_price_presentation = parseFloat(it.unit_price);
            base.presentation_factor     = factor;
            base.presentation_name       = it.presentationName;
          }
          return base;
        })
      };
      await createPurchase(businessId, payload);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-container" style={{ margin: 0, borderRadius: 0 }}>
      {/* Header */}
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <button className="btn-salir" onClick={onClose} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <FiArrowLeft /> Volver
          </button>
          <span className="mt-title" style={{ marginLeft: 8 }}>NUEVA COMPRA</span>
        </div>
      </div>

      <div className="cm-split">
        {/* ══ PANEL IZQUIERDO ══════════════════════════════════ */}
        <div className="cm-left">
          {/* PROVEEDOR */}
          <div className="cm-section">
            <div className="cm-section-title">Proveedor</div>
            <div className="cm-form-grid">
              <div className="cm-field full" style={{ position: 'relative' }}>
                <label className="cm-label">RUC o Nombre<span className="req">*</span></label>
                <input className="cm-input" placeholder="Buscar proveedor..."
                  value={providerSearch}
                  onChange={e => { setProviderSearch(e.target.value); setSelectedProvider(null); }} />
                {provSuggestions.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 180, overflowY: 'auto'
                  }}>
                    {provSuggestions.map(p => (
                      <div key={p.id} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12.5 }}
                        onMouseDown={() => {
                          setSelectedProvider(p);
                          setProviderSearch(p.ruc ? `${p.ruc} — ${p.name}` : p.name);
                          setProvSuggestions([]);
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{p.name}</div>
                        {p.ruc && <div style={{ fontSize: 11, color: '#94a3b8' }}>RUC: {p.ruc}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedProvider && (
                <>
                  <div className="cm-field full">
                    <input className="cm-input cm-input-autofill" disabled value={selectedProvider.name || ''} />
                  </div>
                  {selectedProvider.address && (
                    <div className="cm-field full">
                      <input className="cm-input cm-input-autofill" disabled value={selectedProvider.address || ''} />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* DOCUMENTO */}
          <div className="cm-section">
            <div className="cm-section-title">Documento</div>
            <div className="cm-form-grid">
              <div className="cm-field">
                <label className="cm-label">Tipo Documento<span className="req">*</span></label>
                <select className="cm-select" value={docType} onChange={e => setDocType(e.target.value)}>
                  {DOC_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="cm-field">
                <label className="cm-label">Fecha Compra<span className="req">*</span></label>
                <input type="date" className="cm-input" value={purchaseDate}
                  onChange={e => setPurchaseDate(e.target.value)} />
              </div>
              <div className="cm-field">
                <label className="cm-label">Serie</label>
                <input className="cm-input" maxLength={10}
                  value={serie} onChange={e => setSerie(e.target.value.toUpperCase())}
                  disabled={docType === 'NINGUNO'} />
              </div>
              <div className="cm-field">
                <label className="cm-label">N° Documento</label>
                <input className="cm-input" maxLength={20}
                  value={docNumber} onChange={e => setDocNumber(e.target.value)}
                  disabled={docType === 'NINGUNO'} />
              </div>
            </div>
          </div>

          {/* ALMACÉN Y PAGO */}
          <div className="cm-section">
            <div className="cm-section-title">Destino y Pago</div>
            <div className="cm-form-grid">
              <div className="cm-field full">
                <label className="cm-label">Almacén Destino<span className="req">*</span></label>
                <select className="cm-select" value={warehouseId}
                  onChange={e => setWarehouseId(e.target.value)}>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div className="cm-field full">
                <label className="cm-label">Tipo de Pago<span className="req">*</span></label>
                <div className="cm-pay-toggle">
                  <button type="button"
                    className={`cm-pay-btn ${paymentType === 'paid' ? 'active-contado' : ''}`}
                    onClick={() => setPaymentType('paid')}>
                    CONTADO
                  </button>
                  <button type="button"
                    className={`cm-pay-btn ${paymentType === 'credit' ? 'active-credito' : ''}`}
                    onClick={() => setPaymentType('credit')}>
                    CRÉDITO
                  </button>
                </div>
              </div>
              {paymentType === 'paid' && (
                <div className="cm-field full">
                  <label className="cm-label">Forma de Pago<span className="req">*</span></label>
                  <select className="cm-select" value={paymentMethod}
                    onChange={e => setPaymentMethod(e.target.value)}>
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              )}
              {paymentType === 'credit' && (
                <div className="cm-field full">
                  <label className="cm-label">Fecha Vencimiento<span className="req">*</span></label>
                  <input type="date" className="cm-input" value={dueDate}
                    onChange={e => setDueDate(e.target.value)} />
                </div>
              )}
            </div>
          </div>

          {/* TABLA DE ÍTEMS */}
          <div className="cm-section" style={{ flex: 1, paddingBottom: 0 }}>
            <div className="cm-section-title">Detalle de Compra</div>
            <div style={{ overflowX: 'auto' }}>
              <table className="cm-items-table">
                <thead>
                  <tr>
                    <th style={{ width: 26 }}>#</th>
                    <th>Cod.</th>
                    <th>Producto</th>
                    <th>UM</th>
                    <th style={{ textAlign: 'right' }}>Cantidad</th>
                    <th style={{ textAlign: 'right' }}>P.COMPRA</th>
                    <th style={{ textAlign: 'right' }}>P. Venta Ref.</th>
                    <th style={{ textAlign: 'right' }}>Dscto S/</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 12 }}>
                        Selecciona productos del catálogo →
                      </td>
                    </tr>
                  ) : items.map((it, idx) => (
                    <tr key={idx}>
                      <td style={{ color: '#94a3b8', fontSize: 11 }}>{idx + 1}</td>
                      <td><span style={{ fontFamily: 'monospace', fontSize: 11, background: '#f1f5f9', padding: '1px 5px', borderRadius: 3 }}>{it.sku || '—'}</span></td>
                      <td style={{ fontWeight: 600, fontSize: 12, color: '#1e293b', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.product_name}>{it.product_name}</td>
                      <td style={{ fontSize: 11, color: '#475569' }}>
                        {(it.presentationOptions && it.presentationOptions.length > 0) ? (
                          <select
                            className="cm-item-input"
                            style={{ width: 90 }}
                            value={it.factor > 1 ? String(it.presentationOptions.find(pp => pp.name === it.presentationName)?.id || 'base') : 'base'}
                            onChange={e => changePresentation(idx, e.target.value)}
                          >
                            <option value="base">{it.baseUnitMeasure}</option>
                            {it.presentationOptions.map(pp => (
                              <option key={pp.id} value={pp.id}>{pp.name}</option>
                            ))}
                          </select>
                        ) : it.unit_measure}
                      </td>
                      <td>
                        <input className="cm-item-input w60" type="number" min="0.001" step="0.001"
                          placeholder="0" value={it.qty}
                          onChange={e => updateItem(idx, 'qty', e.target.value)} />
                        {it.special_condition && (
                          <input className="cm-item-input w60" type="number" min="0" step="1"
                            placeholder="und" value={it.qty_unidad}
                            onChange={e => updateItem(idx, 'qty_unidad', e.target.value)}
                            style={{ marginLeft: 4 }} />
                        )}
                      </td>
                      <td>
                        <input className="cm-item-input" type="number" min="0" step="0.01"
                          value={it.unit_price}
                          onChange={e => updateItem(idx, 'unit_price', e.target.value)} />
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 11.5, color: '#64748b' }}>
                        {it.salePriceRef !== undefined ? fmtS(it.salePriceRef) : '—'}
                      </td>
                      <td>
                        <input className="cm-item-input w60" type="number" min="0" step="0.01"
                          value={it.discount}
                          onChange={e => updateItem(idx, 'discount', e.target.value)} />
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1', fontSize: 12 }}>
                        {fmtS(calcItem(it))}
                      </td>
                      <td>
                        <button className="btn-del-item" onClick={() => removeItem(idx)}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* TOTALES */}
          <div className="cm-totals">
            <div className="cm-total-row">
              <span className="cm-total-label">SubTotal:</span>
              <span className="cm-total-val">{fmtS(subtotal + discTotal)}</span>
            </div>
            <div className="cm-total-row">
              <span className="cm-total-label">Descuento Total: -</span>
              <span className="cm-total-val" style={{ color: '#dc2626' }}>{fmtS(discTotal)}</span>
            </div>
            <div className="cm-total-row">
              <span className="cm-total-label">IGV (0%):</span>
              <span className="cm-total-val">S/ 0.00</span>
            </div>
            <div className="cm-total-row grand">
              <span>TOTAL:</span>
              <span style={{ color: '#0369a1' }}>{fmtS(total)}</span>
            </div>
            {paymentType === 'paid' && (
              <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, marginTop: 4, textAlign: 'right' }}>
                ✓ Pago inmediato · {paymentMethod} · Sin deuda pendiente
              </div>
            )}
            {paymentType === 'credit' && (
              <div style={{ fontSize: 11, color: '#d97706', fontWeight: 600, marginTop: 4, textAlign: 'right' }}>
                ⏳ Crédito · Deuda generada{dueDate ? ` · Vcto: ${fmtDateOnly(dueDate)}` : ''}
              </div>
            )}
          </div>

          {/* BOTONES */}
          {error && (
            <div style={{
              margin: '0 16px 4px', padding: '8px 12px', borderRadius: 6,
              background: '#fef2f2', border: '1px solid #fecaca',
              color: '#dc2626', fontSize: 12.5, fontWeight: 600
            }}>
              ⚠ {error}
            </div>
          )}
          <div className="cm-footer">
            <button className="btn-modificar" onClick={handleSave} disabled={saving}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <FiShoppingCart />
              {saving ? 'Guardando...' : 'Guardar Compra'}
            </button>
            <button className="btn-salir" onClick={onClose}>Cerrar</button>
          </div>
        </div>

        {/* ══ PANEL DERECHO — CATÁLOGO ══════════════════════════ */}
        <div className="cm-right">
          <div className="cm-catalog-search">
            <div style={{ fontSize: 11.5, fontWeight: 700, color: '#1e293b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Catálogo de Productos
            </div>
            <div className="cm-catalog-search-wrap">
              <FiSearch className="cm-catalog-icon" />
              <input className="cm-catalog-input" placeholder="Buscar por nombre, código..."
                value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)} />
            </div>
          </div>
          <div style={{ overflowX: 'auto', flex: 1 }}>
            <table className="cm-catalog-table">
              <thead>
                <tr>
                  <th>Cod.</th>
                  <th>Producto</th>
                  <th style={{ textAlign: 'right' }}>P.COMPRA</th>
                  <th style={{ textAlign: 'right' }}>Stock</th>
                </tr>
              </thead>
              <tbody>
                {catalogFiltered.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>Sin resultados</td></tr>
                ) : catalogFiltered.map(p => (
                  <tr key={p.id}
                    className={addedIds.has(p.id) ? 'already-added' : ''}
                    onClick={() => addProduct(p)}>
                    <td><span style={{ fontFamily: 'monospace', fontSize: 11, background: '#f1f5f9', padding: '1px 5px', borderRadius: 3 }}>{p.sku || '—'}</span></td>
                    <td style={{ fontWeight: addedIds.has(p.id) ? 700 : 500, color: addedIds.has(p.id) ? '#16a34a' : '#1e293b', fontSize: 12.5 }}>
                      {p.name}
                      {addedIds.has(p.id) && <span style={{ fontSize: 10, marginLeft: 4, color: '#16a34a' }}>✓ Agregado</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 12.5, color: '#475569' }}>
                      S/ {fmtN(p.cost_price)}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 12, fontWeight: 600,
                      color: parseFloat(p.stock) <= 0 ? '#dc2626' : '#64748b' }}>
                      {fmtN(p.stock, 3)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MODAL IMPRESIÓN COMPRA
// ════════════════════════════════════════════════════════════
function ModalImpresionCompra({ purchaseId, businessId, onClose }) {
  const [detail,  setDetail]  = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getPurchase(purchaseId, businessId),
      getEmpresaInfo(businessId)
    ]).then(([pRes, eRes]) => {
      setDetail(pRes.data?.data   || null);
      setEmpresa(eRes.data?.data  || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [purchaseId, businessId]);

  const empName = empresa?.name    || empresa?.business_name || 'PIVASAPOLLOS';
  const empRuc  = empresa?.ruc     || '';
  const empDir  = empresa?.address || empresa?.direccion || '';

  // ── Generar PDF ──────────────────────────────────────────
  const handlePrint = () => {
    if (!detail) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.width;

    // ── Cabecera empresa ─────────────────────────────────
    doc.setFillColor(13, 27, 46);
    doc.rect(0, 0, pw, 24, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15); doc.setFont('helvetica', 'bold');
    doc.text(empName, 14, 10);
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    if (empRuc) doc.text(`RUC: ${empRuc}`, 14, 15.5);
    if (empDir) doc.text(empDir, 14, 20);

    // Título del documento (derecha)
    doc.setFontSize(11); doc.setFont('helvetica', 'bold');
    doc.setTextColor(14, 165, 233);
    const titulo = detail.document_type === 'NINGUNO'
      ? 'ORDEN DE COMPRA'
      : `${detail.document_type}`;
    doc.text(titulo, pw - 14, 10, { align: 'right' });
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.setTextColor(226, 232, 240);
    if (detail.serie && detail.doc_number) {
      doc.text(`${detail.serie}-${detail.doc_number}`, pw - 14, 15.5, { align: 'right' });
    }
    doc.text(detail.purchase_number, pw - 14, 20, { align: 'right' });

    let y = 30;

    // ── Sección proveedor + datos ────────────────────────
    doc.setFillColor(241, 245, 249);
    doc.rect(10, y, pw - 20, 32, 'F');
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
    doc.text('PROVEEDOR', 14, y + 5);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 41, 59);
    doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.text(detail.provider_name || '—', 14, y + 11);
    doc.setFontSize(8.5); doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    if (detail.provider_ruc)     doc.text(`RUC: ${detail.provider_ruc}`, 14, y + 17);
    if (detail.provider_address) doc.text(detail.provider_address.substring(0, 55), 14, y + 22);

    // Columna derecha
    const col2 = pw / 2 + 5;
    doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(71, 85, 105);
    doc.text('DATOS DE LA COMPRA',  col2, y + 5);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(30, 41, 59);
    doc.text(`Fecha:     ${fmtDate(detail.purchase_date)}`,   col2, y + 11);
    doc.text(`Almacén:   ${detail.warehouse_name || '—'}`,    col2, y + 17);
    doc.text(`Tipo pago: ${detail.payment_status === 'paid' ? 'CONTADO' : 'CRÉDITO'}`, col2, y + 22);
    if (detail.payment_method) doc.text(`Forma pago: ${detail.payment_method}`, col2, y + 27);

    y += 38;

    // ── Tabla de ítems ────────────────────────────────────
    autoTable(doc, {
      startY: y,
      head: [['#', 'Cód.', 'Producto', 'Cant.', 'UM', 'P. Unit.', 'Dto. S/', 'Total']],
      body: (detail.details || []).map((d, i) => [
        i + 1,
        d.sku || d.barcode || '—',
        d.product_name,
        fmtN(d.quantity_presentation ?? d.quantity, 3),
        d.presentation_name || d.unit_measure,
        `S/ ${fmtN(d.unit_price_presentation ?? d.unit_price)}`,
        `S/ ${fmtN(d.discount)}`,
        `S/ ${fmtN(d.subtotal)}`
      ]),
      styles:            { fontSize: 8.5, cellPadding: 2.5, textColor: [51, 65, 85] },
      headStyles:        { fillColor: [13, 27, 46], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles:{ fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 8,  halign: 'center' },
        1: { cellWidth: 20 },
        2: { cellWidth: 72 },
        3: { cellWidth: 16, halign: 'right' },
        4: { cellWidth: 12, halign: 'center' },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 18, halign: 'right' },
        7: { cellWidth: 22, halign: 'right' }
      },
      didDrawPage: (data) => {
        doc.setFontSize(7); doc.setTextColor(148, 163, 184);
        doc.text(`Pág. ${data.pageNumber}`, pw - 14, doc.internal.pageSize.height - 6, { align: 'right' });
      }
    });

    // ── Totales ──────────────────────────────────────────
    let ty = doc.lastAutoTable.finalY + 5;
    const totX = pw - 14;
    const labX = pw - 60;

    doc.setFontSize(8.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105);
    doc.text('SubTotal:',        labX, ty);      doc.text(`S/ ${fmtN(parseFloat(detail.subtotal) + parseFloat(detail.discount))}`, totX, ty, { align: 'right' });
    doc.text('Descuento:  -',    labX, ty + 6);  doc.text(`S/ ${fmtN(detail.discount)}`, totX, ty + 6, { align: 'right' });
    doc.text('IGV (0%):',        labX, ty + 12); doc.text(`S/ ${fmtN(detail.igv)}`,      totX, ty + 12, { align: 'right' });

    doc.setDrawColor(13, 27, 46); doc.line(labX, ty + 15, totX, ty + 15);

    doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(13, 27, 46);
    doc.text('TOTAL:',           labX, ty + 22); doc.text(`S/ ${fmtN(detail.total)}`, totX, ty + 22, { align: 'right' });

    ty += 32;

    // ── Info de pago ──────────────────────────────────────
    doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105);
    if (detail.payment_status === 'paid' && detail.payment_method) {
      doc.text(`Forma de pago: CONTADO — ${detail.payment_method}`, 14, ty);
    } else if (detail.payment_status === 'credit') {
      doc.text('Forma de pago: CRÉDITO', 14, ty);
    }

    ty += 20;

    // ── Líneas de firma ───────────────────────────────────
    const sigY = Math.min(ty + 20, doc.internal.pageSize.height - 30);
    doc.setDrawColor(148, 163, 184);
    doc.line(20, sigY, 80, sigY);
    doc.line(pw - 80, sigY, pw - 20, sigY);
    doc.setFontSize(8); doc.setTextColor(148, 163, 184);
    doc.text('Firma Proveedor', 50, sigY + 5, { align: 'center' });
    doc.text('Firma Recepcionó', pw - 50, sigY + 5, { align: 'center' });

    // ── Footer ────────────────────────────────────────────
    doc.setFontSize(7); doc.setTextColor(148, 163, 184);
    doc.text(
      `Generado: ${new Date().toLocaleDateString('es-PE', { timeZone: 'America/Lima' })}  |  ${empName}  |  ${detail.purchase_number}`,
      pw / 2, doc.internal.pageSize.height - 6, { align: 'center' }
    );

    // Abrir para imprimir
    const blob = doc.output('blob');
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, '_blank');
    if (win) setTimeout(() => win.print(), 700);
  };

  // ── Preview HTML ─────────────────────────────────────────
  return (
    <div className="mt-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="mt-modal" style={{ maxWidth: 680, maxHeight: '90vh', overflow: 'auto' }}>
        <div className="mt-modal-header">
          <span>
            {loading ? 'Cargando...' : `COMPRA ${detail?.purchase_number || ''} — Vista de Impresión`}
          </span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div className="mt-loading">Cargando datos...</div>
        ) : !detail ? (
          <div style={{ padding: 20, color: '#dc2626' }}>No se pudo cargar la compra.</div>
        ) : (
          <div style={{ padding: '0 22px 20px' }}>
            {/* ── Cabecera empresa (preview) ── */}
            <div style={{
              background: '#0d1b2e', color: '#fff', borderRadius: '0 0 8px 8px',
              padding: '14px 18px', marginBottom: 16
            }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{empName}</div>
              {empRuc && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>RUC: {empRuc}</div>}
              {empDir && <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 1 }}>{empDir}</div>}
            </div>

            {/* ── Título doc + N° ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#0ea5e9', textTransform: 'uppercase' }}>
                  {detail.document_type === 'NINGUNO' ? 'ORDEN DE COMPRA' : detail.document_type}
                </div>
                {detail.serie && detail.doc_number && (
                  <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#475569', marginTop: 2 }}>
                    {detail.serie}-{detail.doc_number}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#0369a1' }}>
                  {detail.purchase_number}
                </div>
                <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                  {fmtDate(detail.purchase_date)}
                </div>
              </div>
            </div>

            {/* ── Proveedor + datos ── */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 8, padding: '12px 16px', marginBottom: 14, fontSize: 12.5
            }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>Proveedor</div>
                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 13 }}>{detail.provider_name || '—'}</div>
                {detail.provider_ruc     && <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>RUC: {detail.provider_ruc}</div>}
                {detail.provider_address && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{detail.provider_address}</div>}
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>Datos de la Compra</div>
                <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                  <span style={{ color: '#64748b' }}>Almacén: </span>
                  <span style={{ fontWeight: 600 }}>{detail.warehouse_name || '—'}</span><br />
                  <span style={{ color: '#64748b' }}>Tipo pago: </span>
                  <span style={{ fontWeight: 600 }}>
                    {detail.payment_status === 'paid' ? 'CONTADO' : 'CRÉDITO'}
                    {detail.payment_method ? ` — ${detail.payment_method}` : ''}
                  </span><br />
                  <span style={{ color: '#64748b' }}>Registrado por: </span>
                  <span>{detail.buyer_username || '—'}</span>
                </div>
              </div>
            </div>

            {/* ── Tabla ítems ── */}
            <div className="mt-table-wrap" style={{ marginBottom: 14 }}>
              <table className="mt-table">
                <thead>
                  <tr className="mt-head-row">
                    <th style={{ width: 24, textAlign: 'center' }}>#</th>
                    <th>Cód.</th>
                    <th>Producto</th>
                    <th style={{ textAlign: 'right' }}>Cant.</th>
                    <th>UM</th>
                    <th style={{ textAlign: 'right' }}>P. Unit.</th>
                    <th style={{ textAlign: 'right' }}>Dto. S/</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.details || []).length === 0 ? (
                    <tr className="mt-empty"><td colSpan={8}>Sin ítems</td></tr>
                  ) : (detail.details || []).map((d, i) => (
                    <tr key={d.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                      <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>{i + 1}</td>
                      <td>
                        <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#f1f5f9', padding: '1px 5px', borderRadius: 3 }}>
                          {d.sku || d.barcode || '—'}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, color: '#1e293b' }}>{d.product_name}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(d.quantity_presentation ?? d.quantity, 3)}</td>
                      <td style={{ fontSize: 11.5, color: '#475569' }}>{d.presentation_name || d.unit_measure}</td>
                      <td style={{ textAlign: 'right' }}>S/ {fmtN(d.unit_price_presentation ?? d.unit_price)}</td>
                      <td style={{ textAlign: 'right', color: '#dc2626' }}>S/ {fmtN(d.discount)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>S/ {fmtN(d.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Totales ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <div style={{ minWidth: 220, fontSize: 12.5 }}>
                {[
                  ['SubTotal:', fmtN(parseFloat(detail.subtotal || 0) + parseFloat(detail.discount || 0))],
                  ['Descuento: -', fmtN(detail.discount)],
                  ['IGV (0%):', fmtN(detail.igv)]
                ].map(([lab, val]) => (
                  <div key={lab} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#64748b' }}>
                    <span>{lab}</span><span>S/ {val}</span>
                  </div>
                ))}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  borderTop: '2px solid #0d1b2e', marginTop: 6, paddingTop: 6,
                  fontSize: 15, fontWeight: 800, color: '#0d1b2e'
                }}>
                  <span>TOTAL:</span>
                  <span>S/ {fmtN(detail.total)}</span>
                </div>
              </div>
            </div>

            {/* ── Info pago ── */}
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, padding: '8px 14px', fontSize: 12, color: '#0369a1', marginBottom: 8 }}>
              <b>Forma de pago:</b>{' '}
              {detail.payment_status === 'paid'
                ? `CONTADO${detail.payment_method ? ` — ${detail.payment_method}` : ''}`
                : `CRÉDITO`}
            </div>
          </div>
        )}

        <div className="mt-modal-footer">
          <button className="btn-modificar"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={handlePrint} disabled={loading || !detail}>
            <FiPrinter /> Imprimir / Descargar PDF
          </button>
          <button className="btn-salir" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL — LISTADO
// ════════════════════════════════════════════════════════════
export default function ComprasMaintenance({ businessId }) {
  const [view, setView]             = useState('list'); // 'list' | 'new'
  const [purchases, setPurchases]   = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [selected, setSelected]     = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [anulModal,  setAnulModal]  = useState(null);
  const [printModal, setPrintModal] = useState(null); // purchase id

  const [filters, setFilters] = useState({
    from_date:   '', to_date: '', provider_id: '', status: '', search: ''
  });
  const [searchText, setSearchText] = useState('');
  const [page,    setPage]    = useState(1);
  const [perPage, setPerPage] = useState(25);

  const loadList = useCallback(async (f) => {
    if (!businessId) return;
    setLoading(true);
    try {
      const ef = f || filters;
      const res = await getPurchases(businessId, { ...ef, page, limit: perPage });
      setPurchases(res.data?.rows || []);
      setTotal(res.data?.total   || 0);
    } catch {} finally { setLoading(false); }
  }, [businessId, filters, page, perPage]);

  useEffect(() => { loadList(); }, [businessId, page, perPage]);

  const handleFiltrar = () => {
    const f = { ...filters, search: searchText };
    setFilters(f);
    setPage(1);
    loadList(f);
  };

  const handleSelectRow = async (p) => {
    setSelected(p);
    setSelectedDetail(null);
    try {
      const res = await getPurchase(p.id, businessId);
      setSelectedDetail(res.data?.data || null);
    } catch {}
  };

  if (view === 'new') {
    return (
      <FormNuevaCompra
        businessId={businessId}
        userId={null}
        onSaved={() => { setView('list'); loadList(); }}
        onClose={() => setView('list')}
      />
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="mt-container">
      {/* HEADER */}
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <span className="mt-title">COMPRAS DE MERCADERÍA</span>
        </div>
        <button className="btn-nuevo" onClick={() => setView('new')}>
          <FiPlus /> Nueva Compra
        </button>
      </div>

      {/* FILTROS */}
      <div className="si-filter-bar">
        <div className="si-filter-group">
          <span className="si-label">Desde:</span>
          <input type="date" className="si-input-date" value={filters.from_date}
            onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} />
          <span className="si-label">Hasta:</span>
          <input type="date" className="si-input-date" value={filters.to_date}
            onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} />
          <select className="si-select" value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">Todos los estados</option>
            <option value="completed">Activas</option>
            <option value="cancelled">Anuladas</option>
          </select>
          <div className="si-search-wrap">
            <FiSearch className="si-search-icon" />
            <input className="si-search" placeholder="N° compra, N° doc, proveedor..."
              value={searchText} onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleFiltrar()} />
          </div>
          <button className="btn-nuevo" onClick={handleFiltrar} disabled={loading}>
            Buscar
          </button>
        </div>
      </div>

      {/* RESUMEN */}
      <div className="mt-export-bar">
        <span style={{ fontSize: 12, color: '#64748b' }}>
          {loading ? 'Cargando...' : `${total} compra(s)`}
        </span>
        <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>
          Total: {fmtS(purchases.reduce((s, p) => s + parseFloat(p.total || 0), 0))}
        </span>
        <div className="mt-pagination" style={{ marginLeft: 'auto' }}>
          <button onClick={() => setPage(1)} disabled={page === 1}>|&lt;</button>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>&lt;</button>
          <span className="pg-current">{page}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>&gt;</button>
          <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}>&gt;|</button>
          <span className="pg-info">de {totalPages}</span>
          <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
            {[25, 50, 100].map(n => <option key={n} value={n}>{n}/pág</option>)}
          </select>
        </div>
      </div>

      {/* TABLA */}
      <div className="mt-table-wrap">
        <table className="mt-table">
          <thead>
            <tr className="mt-head-row">
              <th>N° Compra</th>
              <th>Tipo Doc</th>
              <th>N° Doc</th>
              <th>Proveedor</th>
              <th>Almacén</th>
              <th>Fecha</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th>Pago</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="mt-empty"><td colSpan={10}>Cargando...</td></tr>
            ) : purchases.length === 0 ? (
              <tr className="mt-empty"><td colSpan={10}>Sin compras registradas</td></tr>
            ) : purchases.map((p, i) => (
              <tr key={p.id}
                className={`${i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}${p.status === 'cancelled' ? ' mt-row-anulado' : ''}${selected?.id === p.id ? ' selected-row' : ''}`}
                onClick={() => handleSelectRow(p)}
                style={{ cursor: 'pointer' }}>
                <td className={p.status === 'cancelled' ? '' : 'no-strike'}>
                  <span style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700, color: '#0369a1' }}>
                    {p.purchase_number}
                  </span>
                </td>
                <td>{p.document_type}</td>
                <td style={{ fontSize: 11.5, fontFamily: 'monospace' }}>
                  {p.serie && p.doc_number ? `${p.serie}-${p.doc_number}` : p.doc_number || '—'}
                </td>
                <td style={{ fontWeight: 600 }}>{p.provider_name || '—'}</td>
                <td style={{ fontSize: 11.5, color: '#64748b' }}>{p.warehouse_name || '—'}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{fmtDate(p.purchase_date)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>
                  {fmtS(p.total)}
                </td>
                <td className="no-strike"><BadgePago status={p.payment_status} /></td>
                <td className="no-strike"><BadgeEstado status={p.status} /></td>
                <td className="td-actions no-strike" style={{ display: 'flex', gap: 4 }}>
                  <button className="btn-row-edit"
                    title="Ver e imprimir compra"
                    onClick={e => { e.stopPropagation(); setPrintModal(p.id); }}>
                    <FiPrinter />
                  </button>
                  {p.status !== 'cancelled' && (
                    <button className="btn-row-del"
                      title="Anular compra"
                      onClick={e => { e.stopPropagation(); setAnulModal(p.id); }}>
                      <FiTrash2 />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* DETALLE DE COMPRA SELECCIONADA */}
      {selected && (
        <div className="cm-detail-panel">
          <div className="cm-detail-header">
            <span>DETALLE — {selected.purchase_number}</span>
            <span style={{ opacity: 0.6 }}>|</span>
            <span>{selected.provider_name}</span>
            <span style={{ opacity: 0.6 }}>|</span>
            <span>{selected.warehouse_name}</span>
            <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 10.5 }}>
              clic en otra fila para cambiar
            </span>
          </div>
          {!selectedDetail ? (
            <div className="mt-loading" style={{ padding: 16, fontSize: 12 }}>Cargando detalle...</div>
          ) : (
            <div className="mt-table-wrap">
              <table className="mt-table">
                <thead>
                  <tr className="mt-head-row">
                    <th>Item</th>
                    <th>Cod.</th>
                    <th>Producto</th>
                    <th>UM</th>
                    <th style={{ textAlign: 'right' }}>Cantidad</th>
                    <th style={{ textAlign: 'right' }}>P. Unitario</th>
                    <th style={{ textAlign: 'right' }}>Descuento</th>
                    <th style={{ textAlign: 'right' }}>IGV</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDetail.details?.map((d, i) => (
                    <tr key={d.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                      <td style={{ color: '#94a3b8' }}>{i + 1}</td>
                      <td><span style={{ fontFamily: 'monospace', fontSize: 11.5, background: '#f1f5f9', padding: '1px 5px', borderRadius: 3 }}>{d.sku || d.barcode || '—'}</span></td>
                      <td style={{ fontWeight: 600 }}>{d.product_name}</td>
                      <td>{d.presentation_name || d.unit_measure}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>
                        {fmtN(d.quantity_presentation ?? d.quantity, 3)}
                        {d.qty_unidad > 0 && <span style={{ fontSize: 10.5, color: '#64748b' }}> ({d.qty_unidad} uds)</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>S/ {fmtN(d.unit_price_presentation ?? d.unit_price)}</td>
                      <td style={{ textAlign: 'right', color: '#dc2626' }}>S/ {fmtN(d.discount)}</td>
                      <td style={{ textAlign: 'right' }}>S/ {fmtN(d.igv)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>S/ {fmtN(d.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f1f5f9' }}>
                    <td colSpan={8} style={{ textAlign: 'right', fontWeight: 700, fontSize: 11.5, padding: '7px 12px', color: '#475569' }}>
                      TOTAL:
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#0369a1', padding: '7px 12px', fontSize: 13 }}>
                      {fmtS(selectedDetail.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* MODAL ANULACIÓN */}
      {anulModal && (
        <ModalAnulacion
          purchaseId={anulModal}
          businessId={businessId}
          onClose={() => setAnulModal(null)}
          onAnulado={() => { setAnulModal(null); setSelected(null); setSelectedDetail(null); loadList(); }}
        />
      )}

      {/* MODAL IMPRESIÓN */}
      {printModal && (
        <ModalImpresionCompra
          purchaseId={printModal}
          businessId={businessId}
          onClose={() => setPrintModal(null)}
        />
      )}
    </div>
  );
}
